import { FetchResult, gql, useMutation, useQuery } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';
import { useState } from 'react';

import {
  GraphVariant,
  PersistedQueryList,
  UsePQBatchLinkVariantsQuery,
  UsePQBatchLinkVariantsQueryVariables,
  UsePQLinkVariantMutation,
  UsePQLinkVariantMutationVariables,
  UsePQUnlinkVariantMutation,
  UsePQUnlinkVariantMutationVariables,
} from 'src/lib/graphqlTypes/types';

export const usePQBatchLinkVariantsQuery = gql<
  UsePQBatchLinkVariantsQuery,
  UsePQBatchLinkVariantsQueryVariables
>`
  query UsePQBatchLinkVariantsQuery($graphId: ID!, $listId: ID!) {
    graph(id: $graphId) {
      id
      title
      variants {
        id
        name
      }
      persistedQueryList(id: $listId) {
        id
        name
        linkedVariants {
          id
          name
        }
      }
    }
  }
`;

export const usePQLinkVariantMutation = gql<
  UsePQLinkVariantMutation,
  UsePQLinkVariantMutationVariables
>`
  mutation UsePQLinkVariantMutation(
    $graphId: ID!
    $listId: ID!
    $variantName: String!
  ) {
    graph(id: $graphId) {
      variant(name: $variantName) {
        id
        name
        linkResult: linkPersistedQueryList(persistedQueryListId: $listId) {
          ... on LinkPersistedQueryListResult {
            persistedQueryList {
              id
            }
          }
          ... on ListNotFoundError {
            message
          }
          ... on PermissionError {
            message
          }
          ... on VariantAlreadyLinkedError {
            message
          }
        }
      }
    }
  }
`;

export const usePQUnlinkVariantMutation = gql<
  UsePQUnlinkVariantMutation,
  UsePQUnlinkVariantMutationVariables
>`
  mutation UsePQUnlinkVariantMutation($graphId: ID!, $variantName: String!) {
    graph(id: $graphId) {
      variant(name: $variantName) {
        id
        name
        linkResult: unlinkPersistedQueryList {
          ... on UnlinkPersistedQueryListResult {
            unlinkedPersistedQueryList {
              id
            }
          }
          ... on PermissionError {
            message
          }
          ... on VariantAlreadyUnlinkedError {
            message
          }
        }
      }
    }
  }
`;

export interface UsePQBatchLinkVariantsFailureMessage {
  variantName?: string;
  message: string;
}

export interface UsePQBatchLinkVariantsResult {
  batchLinkVariants: (updatedLinkedVariantNames: Set<string>) => Promise<void>;
  loading: boolean;
  submitting: boolean;
  errors: GraphQLFormattedError[];
  persistedQueryList?:
    | (Pick<PersistedQueryList, 'id' | 'name'> & {
        linkedVariants: Pick<GraphVariant, 'id' | 'name'>[];
      })
    | null;
  linkedVariants?: Pick<GraphVariant, 'id' | 'name'>[];
  allVariants?: Pick<GraphVariant, 'id' | 'name'>[];
  unlinkResults?: FetchResult<UsePQUnlinkVariantMutation>[];
  linkResults?: FetchResult<UsePQLinkVariantMutation>[];
  failureMessages: UsePQBatchLinkVariantsFailureMessage[];
}

interface BatchLinkFailures {
  failureMessages: UsePQBatchLinkVariantsFailureMessage[];
  errors: GraphQLFormattedError[];
}

/**
 * Accumulate fetch results according to the failure type.
 * For a given fetch result, if it had an error, add it to the errors list.
 * If it had a non-success "errors as data" fetch response, add it to the failure messages list.
 */
function accumulateFailureOrError(
  prevFailures: BatchLinkFailures,
  result:
    | FetchResult<UsePQUnlinkVariantMutation>
    | FetchResult<UsePQLinkVariantMutation>,
) {
  const { failureMessages, errors } = prevFailures;
  if (result.errors) {
    return {
      failureMessages,
      errors: [...result.errors, ...errors],
    };
  }
  const variant = result.data?.graph?.variant;
  if (
    variant &&
    // There's a little magic here as we've used the same alias for both
    // mutations to return the field as 'linkResult'
    // instead of linkPersistedQueryList / unlinkPersistedQueryList.
    variant.linkResult.__typename !== 'UnlinkPersistedQueryListResult' &&
    variant.linkResult.__typename !== 'LinkPersistedQueryListResult'
  ) {
    return {
      failureMessages: [
        {
          variantName: variant.name,
          message: variant.linkResult.message,
        },
        ...failureMessages,
      ],
      errors,
    };
  }
  return prevFailures;
}

/**
 * Transform failure messages into more convenient form for the UI.
 */
export function mapResultsToFailureMessagesAndErrors({
  unlinkResults,
  linkResults,
}: {
  unlinkResults?: FetchResult<UsePQUnlinkVariantMutation>[];
  linkResults?: FetchResult<UsePQLinkVariantMutation>[];
}): BatchLinkFailures {
  return [
    ...(unlinkResults ?? ([] as FetchResult<UsePQUnlinkVariantMutation>[])),
    ...(linkResults ?? ([] as FetchResult<UsePQLinkVariantMutation>[])),
  ].reduce(
    (prevFailures, result) => accumulateFailureOrError(prevFailures, result),
    {
      failureMessages: [],
      errors: [],
    } as BatchLinkFailures,
  );
}

export const usePQBatchLinkVariants = ({
  graphId,
  listId,
}: {
  graphId: string;
  listId: string;
}): UsePQBatchLinkVariantsResult => {
  const [submitting, setSubmitting] = useState(false);
  const {
    data: queryData,
    loading: queryLoading,
    refetch: refetchQueryData,
  } = useQuery(usePQBatchLinkVariantsQuery, {
    variables: {
      graphId,
      listId,
    },
  });

  const [mutateLink] = useMutation(usePQLinkVariantMutation);
  const [mutateUnlink] = useMutation(usePQUnlinkVariantMutation);
  const [unlinkResults, setUnlinkResults] = useState<
    FetchResult<UsePQUnlinkVariantMutation>[] | undefined
  >();
  const [linkResults, setLinkResults] = useState<
    FetchResult<UsePQLinkVariantMutation>[] | undefined
  >();

  const allVariants = queryData?.graph?.variants;
  const linkedVariants = queryData?.graph?.persistedQueryList?.linkedVariants;
  const persistedQueryList = queryData?.graph?.persistedQueryList;

  const batchLinkVariants = async (updatedLinkedVariantNames: Set<string>) => {
    const variantsToRemove = linkedVariants
      ? new Set<string>([
          ...linkedVariants
            .map((v) => v.name)
            .filter((name) => !updatedLinkedVariantNames.has(name)),
        ])
      : new Set<string>();
    const variantsToAdd = linkedVariants
      ? new Set<string>(
          [...updatedLinkedVariantNames].filter(
            (name) => !linkedVariants.find((v) => v.name === name),
          ),
        )
      : new Set<string>();

    const unlinkPromises = [...variantsToRemove].map((name) =>
      mutateUnlink({
        variables: {
          graphId,
          variantName: name,
        },
      }),
    );

    const linkPromises = [...variantsToAdd].map((name) =>
      mutateLink({
        variables: {
          graphId,
          listId,
          variantName: name,
        },
      }),
    );

    try {
      // reset the current state
      setSubmitting(true);
      setUnlinkResults(undefined);
      setLinkResults(undefined);

      // queue and wait for all mutations
      const unlinkPromiseResults = await Promise.all(unlinkPromises);
      const linkPromiseResults = await Promise.all(linkPromises);

      // save our results
      setUnlinkResults(unlinkPromiseResults);
      setLinkResults(linkPromiseResults);

      // also refetch the latest data when all is finished to ensure we're in sync
      await refetchQueryData();
    } finally {
      setSubmitting(false);
    }
  };

  const { failureMessages, errors } = mapResultsToFailureMessagesAndErrors({
    unlinkResults,
    linkResults,
  });

  const result = {
    batchLinkVariants,
    loading: queryLoading,
    submitting,
    errors,
    persistedQueryList,
    allVariants,
    linkedVariants,
    unlinkResults,
    linkResults,
    failureMessages,
  };
  return result;
};
